import type {CallIntent, Reaction} from '@phonehq/rtc-core';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {loadDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {runCallControl} from '../../groupCalls/components/groupCallActions';

export type ShareFailure = 'denied' | 'failed';

const USER_CANCELLED = /permission denied$/i;

export const classifyShareFailure = (error: unknown): ShareFailure | undefined => {
    const name = error instanceof Error ? error.name : '';
    const message = error instanceof Error ? error.message : String(error);
    if (name === 'NotAllowedError' || name === 'SecurityError') {
        return USER_CANCELLED.test(message.trim()) || /cancel|dismiss/i.test(message) ? undefined : 'denied';
    }
    if (name === 'AbortError') {
        return undefined;
    }
    return 'failed';
};

export const groupActions = {
    setMicrophone: (enabled: boolean) => void runCallControl('microphone', () => groupCallService.setMicrophone(enabled)),
    setCamera: (enabled: boolean) => void runCallControl('camera', () => groupCallService.setCamera(enabled)),
    setHandRaised: (up: boolean) => void runCallControl('hand', async () => groupCallService.controller?.setHandRaised(up)),
    react: (reaction: Reaction) => void runCallControl('reaction', () => groupCallService.sendReaction(reaction)),
    leave: () => void runCallControl('leave', () => groupCallService.leave()),
    expand: () => groupCallService.setMinimized(false),
    minimize: () => groupCallService.setMinimized(true),
    answer: (intent: CallIntent) =>
        void runCallControl('answer', () => groupCallService.answerIncoming(intent, loadDevicePreferences())),
    decline: () => void runCallControl('decline', () => groupCallService.declineIncoming()),
    declineInvite: () => void runCallControl('decline_invite', () => groupCallService.declineInvite()),
    acceptInvite: (expanded: boolean) =>
        void runCallControl('accept_invite', () => groupCallService.acceptInvite(loadDevicePreferences(), expanded)),
    retrySwitch: () => groupCallService.retrySwitch(loadDevicePreferences()),
    dismissSwitchFailure: () => groupCallService.dismissSwitchFailure(),
    dismiss: () => groupCallService.dismiss(),
    stopScreenShare: () => void runCallControl('screen_share', async () => groupCallService.controller?.setScreenShare(false)),
    switchDevice: (kind: MediaDeviceKind, deviceId: string) => void runCallControl('switch_device', () => groupCallService.switchDevice(kind, deviceId)),
    startScreenShare: async (): Promise<ShareFailure | undefined> => {
        const controller = groupCallService.controller;
        if (!controller) {
            return 'failed';
        }
        try {
            await controller.setScreenShare(true);
            return undefined;
        } catch (error) {
            return classifyShareFailure(error);
        }
    },
    rejoin: (roomId: string, intent: CallIntent) =>
        void runCallControl('rejoin', async () => {
            if (await groupCallService.openLobby(roomId, intent)) {
                await groupCallService.join({
                    intent,
                    microphoneEnabled: true,
                    cameraEnabled: intent === 'video',
                    devices: loadDevicePreferences(),
                });
            }
        }),
};
